// Universal Console (Elakay Academy) -- format detection, safe conversions
// and import analysis for whatever an admin pastes or imports:
//   complete HTML documents, HTML fragments, plain text, Markdown, the
//   Console JSON document, with or without Elakay metadata.
//
// Rules this module enforces:
//  - plain text is never mistaken for HTML (only real, known tag names count);
//  - nothing is removed silently: unknown/blocked/inert elements are reported
//    as warnings, the original source is always what gets stored;
//  - Markdown and plain text are converted to HTML with every author
//    character escaped (no raw HTML, no javascript:/data: links);
//  - size limits mirror the MongoDB schema (elakay_models/howWeDoIt.js).
import { parseVideoUrl, isValidVideoRef, buildVideoEmbedUrl } from "./howWeDoItVideoUrl";

// Same id as howWeDoItPageDocument.js PAGE_METADATA_SCRIPT_ID (kept local to
// avoid an import cycle: that module imports this one).
const PAGE_METADATA_SCRIPT_ID = "elakay-page-metadata";

export const HWDI_LIMITS = Object.freeze({
  htmlCode: 500000,
  customCss: 200000,
  customJs: 200000,
  sourceText: 500000,
});

// nginx in front of production currently refuses request bodies over 1 MiB
// (ELAKAY_LOCAL_VS_PRODUCTION.md, LP-01); the API itself accepts 2 MB.
export const PRODUCTION_REQUEST_LIMIT_BYTES = 1024 * 1024;

export const SOURCE_FORMATS = Object.freeze(["", "html", "markdown", "text"]);

// Sources allowed for external stylesheets / scripts / fonts inside the
// isolated preview (also enforced by the frame's Content-Security-Policy).
export const ALLOWED_STYLE_HOSTS = Object.freeze([
  "fonts.googleapis.com",
  "cdnjs.cloudflare.com",
  "cdn.jsdelivr.net",
  "unpkg.com",
]);
export const ALLOWED_SCRIPT_HOSTS = Object.freeze(["cdnjs.cloudflare.com", "cdn.jsdelivr.net", "unpkg.com"]);
export const ALLOWED_FONT_HOSTS = Object.freeze(["fonts.gstatic.com", "cdnjs.cloudflare.com", "cdn.jsdelivr.net", "unpkg.com"]);

const KNOWN_TAGS = new Set(
  (
    "a abbr address area article aside audio b base bdi bdo blockquote body br button canvas caption cite code col colgroup " +
    "data datalist dd del details dfn dialog div dl dt em embed fieldset figcaption figure footer form h1 h2 h3 h4 h5 h6 head " +
    "header hgroup hr html i iframe img input ins kbd label legend li link main map mark menu meta meter nav noscript object ol " +
    "optgroup option output p param picture pre progress q rp rt ruby s samp script search section select slot small source span " +
    "strong style sub summary sup table tbody td template textarea tfoot th thead time title tr track u ul var video wbr " +
    // SVG / MathML elements that may appear inside pasted markup
    "svg path g circle rect line polyline polygon ellipse defs use symbol text tspan lineargradient radialgradient stop clippath mask " +
    "pattern filter image foreignobject desc marker math mi mn mo mrow msup msub mfrac"
  ).split(/\s+/)
);

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

export const byteLength = (value = "") => {
  const text = String(value || "");
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(text).length;
  return unescape(encodeURIComponent(text)).length;
};

export const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const hostOf = (value) => {
  try {
    return new URL(String(value), "https://www.elakay.com").hostname.toLowerCase();
  } catch {
    return "";
  }
};

const isAbsoluteUrl = (value) => /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(String(value || "").trim());

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

const countKnownTags = (text) => {
  let count = 0;
  const pattern = /<\/?([a-zA-Z][a-zA-Z0-9-]*)(?=[\s/>])[^<>]*>/g;
  let match;
  while ((match = pattern.exec(text))) {
    const name = match[1].toLowerCase();
    if (KNOWN_TAGS.has(name) || name.includes("-")) count += 1;
  }
  return count;
};

const markdownScore = (text) => {
  let score = 0;
  const lines = text.split(/\r?\n/);
  let listItems = 0;
  lines.forEach((line) => {
    if (/^\s{0,3}#{1,6}\s+\S/.test(line)) score += 2;
    if (/^\s{0,3}```/.test(line)) score += 2;
    if (/^\s{0,3}>\s?\S/.test(line)) score += 1;
    if (/^\s*([-*+]|\d+[.)])\s+\S/.test(line)) listItems += 1;
    if (/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line)) score += 2;
  });
  score += Math.min(listItems, 3);
  if (/\[[^\]\n]+\]\((?:https?:\/\/|\/|#|mailto:)[^)\s]*\)/.test(text)) score += 2;
  if (/(\*\*|__)[^\s*_][^*_\n]*\1/.test(text)) score += 1;
  if (/!\[[^\]\n]*\]\([^)\s]+\)/.test(text)) score += 2;
  return score;
};

/**
 * @returns {{format: "empty"|"elakay-json"|"html-document"|"html-fragment"|"markdown"|"plain-text", parsedJson?: object}}
 */
export const detectContentFormat = (input) => {
  const text = String(input ?? "");
  const trimmed = text.trim();
  if (!trimmed) return { format: "empty" };
  if (/^[{[]/.test(trimmed)) {
    try {
      const parsed = JSON.parse(trimmed);
      if (isPlainObject(parsed)) return { format: "elakay-json", parsedJson: parsed };
    } catch {
      // not JSON -- keep detecting
    }
  }
  if (/^\s*(?:<!--[\s\S]*?-->\s*)*<!doctype\s+html/i.test(text) || /<html[\s>]/i.test(text)) {
    return { format: "html-document" };
  }
  const tags = countKnownTags(text);
  const md = markdownScore(text);
  if (tags >= 2 && tags >= md) return { format: "html-fragment" };
  if (md >= 2) return { format: "markdown" };
  if (tags >= 1) return { format: "html-fragment" };
  return { format: "plain-text" };
};

// ---------------------------------------------------------------------------
// Safe conversions (Markdown / plain text -> HTML)
// ---------------------------------------------------------------------------

const SAFE_LINK = /^(?:https?:\/\/|mailto:|tel:|\/(?!\/)|\.{0,2}\/|#)/i;
const SAFE_IMAGE = /^(?:https?:\/\/|\/(?!\/)|\.{0,2}\/|data:image\/(?:png|jpe?g|gif|webp);base64,)/i;

const safeHref = (url) => (SAFE_LINK.test(String(url || "").trim()) ? String(url).trim() : "");
const safeSrc = (url) => (SAFE_IMAGE.test(String(url || "").trim()) ? String(url).trim() : "");

const slugifyHeading = (text, used) => {
  const base =
    String(text)
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/<[^>]*>/g, "")
      .replace(/&[a-z]+;/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "section";
  let slug = base;
  let n = 2;
  while (used.has(slug)) {
    slug = `${base}-${n}`;
    n += 1;
  }
  used.add(slug);
  return slug;
};

// Inline Markdown on an ALREADY ESCAPED string.
const renderInline = (escaped) => {
  const codeSpans = [];
  let text = escaped.replace(/`([^`\n]+)`/g, (_m, code) => {
    codeSpans.push(`<code>${code}</code>`);
    return `\uE000${codeSpans.length - 1}\uE000`;
  });
  const unescapeAttr = (value) => value.replace(/&amp;/g, "&");
  text = text.replace(/!\[([^\]\n]*)\]\(([^)\s]+)(?:\s+&quot;([^"\n]*?)&quot;)?\)/g, (_m, alt, url, title) => {
    const src = safeSrc(unescapeAttr(url));
    if (!src) return alt;
    return `<img src="${escapeHtml(src)}" alt="${alt}"${title ? ` title="${title}"` : ""} loading="lazy" />`;
  });
  text = text.replace(/\[([^\]\n]+)\]\(([^)\s]+)(?:\s+&quot;([^"\n]*?)&quot;)?\)/g, (_m, label, url, title) => {
    const href = safeHref(unescapeAttr(url));
    if (!href) return label;
    const external = /^https?:\/\//i.test(href);
    return `<a href="${escapeHtml(href)}"${title ? ` title="${title}"` : ""}${external ? ' target="_blank" rel="noopener noreferrer"' : ""}>${label}</a>`;
  });
  text = text.replace(/&lt;(https?:\/\/[^\s<>]+?)&gt;/g, (_m, url) => {
    const href = unescapeAttr(url);
    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
  text = text
    .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, "<strong>$2</strong>")
    .replace(/(^|[^*\w])\*(?=\S)([^*\n]*?\S)\*(?!\*)/g, "$1<em>$2</em>")
    .replace(/(^|[^_\w])_(?=\S)([^_\n]*?\S)_(?!_)/g, "$1<em>$2</em>")
    .replace(/~~(?=\S)([\s\S]*?\S)~~/g, "<del>$1</del>");
  return text.replace(/\uE000(\d+)\uE000/g, (_m, index) => codeSpans[Number(index)]);
};

const videoEmbedHtml = (ref) => {
  const src = buildVideoEmbedUrl(ref);
  if (!src) return "";
  return `<div class="hwdi-md-video"><iframe src="${escapeHtml(src)}" title="Video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen loading="lazy"></iframe></div>`;
};

const splitTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

// Nested list tree from raw list lines (indentation = nesting level).
const buildListTree = (lines) => {
  const root = { indent: -1, items: [] };
  const stack = [root];
  let lastItem = null;
  lines.forEach((line) => {
    const match = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (!match) {
      if (lastItem) lastItem.text += ` ${line.trim()}`;
      return;
    }
    const indent = match[1].replace(/\t/g, "    ").length;
    while (stack.length > 1 && indent <= stack[stack.length - 1].indent) stack.pop();
    const item = { text: match[3], ordered: /\d/.test(match[2]), children: [] };
    stack[stack.length - 1].items.push(item);
    stack.push({ indent, items: item.children });
    lastItem = item;
  });
  return root.items;
};

const renderListItem = (item) => {
  let content = item.text;
  let prefix = "";
  const task = /^\[([ xX])\]\s+/.exec(content);
  if (task) {
    prefix = `<input type="checkbox" disabled${task[1].trim() ? " checked" : ""} /> `;
    content = content.slice(task[0].length);
  }
  // eslint-disable-next-line no-use-before-define
  return `<li>${prefix}${renderInline(escapeHtml(content))}${item.children.length ? renderListItems(item.children) : ""}</li>`;
};

// Consecutive items of the same kind share one <ul>/<ol>.
const renderListItems = (items) => {
  let html = "";
  let run = [];
  const flush = () => {
    if (!run.length) return;
    const tag = run[0].ordered ? "ol" : "ul";
    html += `<${tag}>${run.map(renderListItem).join("")}</${tag}>`;
    run = [];
  };
  items.forEach((item) => {
    if (run.length && run[0].ordered !== item.ordered) flush();
    run.push(item);
  });
  flush();
  return html;
};

const renderListBlock = (lines) => renderListItems(buildListTree(lines));

/**
 * Markdown -> HTML fragment. Every author character is escaped first: raw
 * HTML inside Markdown is shown as text, links/images are limited to safe
 * schemes, a YouTube/Vimeo address alone on its line becomes a video embed.
 */
export const markdownToHtml = (markdown = "", { embedVideos = true } = {}) => {
  const lines = String(markdown || "").replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  const usedIds = new Set();
  let i = 0;
  const isBlank = (line) => !line || !line.trim();

  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      i += 1;
      continue;
    }

    const fence = /^\s{0,3}(```|~~~)\s*([\w+-]*)\s*$/.exec(line);
    if (fence) {
      const body = [];
      i += 1;
      while (i < lines.length && !new RegExp(`^\\s{0,3}${fence[1]}\\s*$`).test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1;
      const lang = fence[2] ? ` class="language-${escapeHtml(fence[2])}"` : "";
      out.push(`<pre><code${lang}>${escapeHtml(body.join("\n"))}</code></pre>`);
      continue;
    }

    const heading = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const inner = renderInline(escapeHtml(heading[2]));
      out.push(`<h${level} id="${slugifyHeading(heading[2], usedIds)}">${inner}</h${level}>`);
      i += 1;
      continue;
    }

    if (/^\s{0,3}([-*_])(\s*\1){2,}\s*$/.test(line)) {
      out.push("<hr />");
      i += 1;
      continue;
    }

    if (/^\s{0,3}>/.test(line)) {
      const body = [];
      while (i < lines.length && /^\s{0,3}>/.test(lines[i])) {
        body.push(lines[i].replace(/^\s{0,3}>\s?/, ""));
        i += 1;
      }
      out.push(`<blockquote>${markdownToHtml(body.join("\n"), { embedVideos })}</blockquote>`);
      continue;
    }

    if (/\|/.test(line) && i + 1 < lines.length && /\|/.test(lines[i + 1]) && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(lines[i + 1])) {
      const headers = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1]).map((cell) =>
        /^:-+:$/.test(cell) ? "center" : /-+:$/.test(cell) ? "right" : /^:-+/.test(cell) ? "left" : ""
      );
      i += 2;
      const rows = [];
      while (i < lines.length && /\|/.test(lines[i]) && !isBlank(lines[i])) {
        rows.push(splitTableRow(lines[i]));
        i += 1;
      }
      const cellStyle = (index) => (aligns[index] ? ` style="text-align:${aligns[index]}"` : "");
      const head = headers.map((cell, index) => `<th${cellStyle(index)}>${renderInline(escapeHtml(cell))}</th>`).join("");
      const body = rows
        .map((row) => `<tr>${headers.map((_h, index) => `<td${cellStyle(index)}>${renderInline(escapeHtml(row[index] || ""))}</td>`).join("")}</tr>`)
        .join("");
      out.push(`<div class="hwdi-table-scroll"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`);
      continue;
    }

    if (/^\s*([-*+]|\d+[.)])\s+\S/.test(line)) {
      const block = [];
      while (i < lines.length && !isBlank(lines[i]) && (/^\s*([-*+]|\d+[.)])\s+/.test(lines[i]) || /^\s{2,}\S/.test(lines[i]))) {
        block.push(lines[i]);
        i += 1;
      }
      out.push(renderListBlock(block));
      continue;
    }

    if (embedVideos) {
      const ref = parseVideoUrl(line.trim());
      if (/^\s*https?:\/\/\S+\s*$/.test(line) && isValidVideoRef(ref)) {
        out.push(videoEmbedHtml(ref));
        i += 1;
        continue;
      }
    }

    const paragraph = [];
    while (
      i < lines.length &&
      !isBlank(lines[i]) &&
      !/^\s{0,3}(#{1,6}\s|```|~~~|>|([-*_])(\s*\2){2,}\s*$)/.test(lines[i]) &&
      !/^\s*([-*+]|\d+[.)])\s+\S/.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i += 1;
    }
    const html = paragraph
      .map((part, index) => {
        const hardBreak = /( {2,}|\\)$/.test(part) && index < paragraph.length - 1;
        return renderInline(escapeHtml(part.replace(/( {2,}|\\)$/, ""))) + (hardBreak ? "<br />" : "");
      })
      .join("\n");
    out.push(`<p>${html}</p>`);
  }

  return out.join("\n");
};

const linkifyEscaped = (escaped) =>
  escaped.replace(/\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/g, (url) => {
    const href = url.replace(/&amp;/g, "&");
    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });

/** Plain text -> HTML fragment: paragraphs, line breaks, safe links. */
export const plainTextToHtml = (text = "") => {
  const paragraphs = String(text || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n{2,}/)
    .map((part) => part.replace(/^\n+|\n+$/g, ""))
    .filter((part) => part.trim());
  const body = paragraphs.map((part) => `<p>${linkifyEscaped(escapeHtml(part)).replace(/\n/g, "<br />\n")}</p>`).join("\n");
  return `<article class="hwdi-prose hwdi-plain-text">\n${body}\n</article>`;
};

/** Markdown source -> stored HTML (wrapped for the shared prose styles). */
export const markdownToStoredHtml = (markdown = "") =>
  `<article class="hwdi-prose hwdi-markdown">\n${markdownToHtml(markdown)}\n</article>`;

// ---------------------------------------------------------------------------
// HTML analysis (what the import panel and the Console info strip report)
// ---------------------------------------------------------------------------

const parseHtml = (source, asFragment) => {
  if (typeof DOMParser === "undefined") return null;
  const parser = new DOMParser();
  if (asFragment) return parser.parseFromString(`<!doctype html><html><head></head><body>${source}</body></html>`, "text/html");
  return parser.parseFromString(String(source || ""), "text/html");
};

const isClassicScript = (script) => {
  const type = (script.getAttribute("type") || "").trim().toLowerCase();
  return !type || type === "text/javascript" || type === "application/javascript" || type === "module";
};

/**
 * Detailed, read-only report on an HTML document or fragment.
 */
export const analyzeHtml = (source, { fragment = false } = {}) => {
  const doc = parseHtml(source, fragment);
  const report = {
    hasElakayRoot: false,
    hasElakayMetadata: false,
    metadataValid: false,
    metadataBlocks: 0,
    blockMarkers: 0,
    headings: 0,
    sections: 0,
    styles: 0,
    stylesheets: [],
    inlineScripts: 0,
    externalScripts: [],
    dataScripts: 0,
    iframes: [],
    videos: 0,
    images: 0,
    tables: 0,
    forms: 0,
    svgs: 0,
    inlineHandlers: 0,
    customElements: [],
    unknownElements: [],
    hasBase: false,
    hasMetaRefresh: false,
  };
  if (!doc) return report;

  report.hasElakayRoot = !!doc.querySelector("[data-elakay-how-we-do-it-root]");
  report.blockMarkers = doc.querySelectorAll("[data-elakay-block-id]").length;
  const metadata = doc.getElementById(PAGE_METADATA_SCRIPT_ID);
  if (metadata) {
    report.hasElakayMetadata = true;
    try {
      const parsed = JSON.parse(metadata.textContent || "");
      if (isPlainObject(parsed) && Array.isArray(parsed.contentBlocks)) {
        report.metadataValid = true;
        report.metadataBlocks = parsed.contentBlocks.length;
      }
    } catch {
      report.metadataValid = false;
    }
  }
  report.headings = doc.querySelectorAll("h1, h2, h3").length;
  report.sections = doc.querySelectorAll("section, article").length;
  report.styles = doc.querySelectorAll("style").length;
  report.stylesheets = [...doc.querySelectorAll('link[rel~="stylesheet"]')].map((link) => {
    const href = link.getAttribute("href") || "";
    const host = isAbsoluteUrl(href) ? hostOf(href) : "";
    return { href, host, allowed: !host || ALLOWED_STYLE_HOSTS.includes(host) };
  });
  [...doc.querySelectorAll("script")].forEach((script) => {
    const src = script.getAttribute("src");
    if (src) {
      const host = isAbsoluteUrl(src) ? hostOf(src) : "";
      report.externalScripts.push({ src, host, allowed: !!host && ALLOWED_SCRIPT_HOSTS.includes(host) });
    } else if (isClassicScript(script)) {
      if ((script.textContent || "").trim()) report.inlineScripts += 1;
    } else {
      report.dataScripts += 1;
    }
  });
  report.iframes = [...doc.querySelectorAll("iframe")].map((frame) => {
    const src = frame.getAttribute("src") || "";
    const ref = parseVideoUrl(src);
    if (ref && isValidVideoRef(ref)) return { src, kind: "video", provider: ref.provider };
    if (ref) return { src, kind: "invalid-video", provider: ref.provider };
    return { src, kind: "blocked", host: hostOf(src) };
  });
  report.videos = doc.querySelectorAll("video").length;
  report.images = doc.querySelectorAll("img, picture").length;
  report.tables = doc.querySelectorAll("table").length;
  report.forms = doc.querySelectorAll("form").length;
  report.svgs = doc.querySelectorAll("svg").length;
  report.hasBase = !!doc.querySelector("base");
  report.hasMetaRefresh = !!doc.querySelector('meta[http-equiv="refresh" i]');
  const custom = new Set();
  const unknown = new Set();
  doc.querySelectorAll("*").forEach((element) => {
    const name = element.localName;
    if (name.includes("-")) custom.add(name);
    else if (!KNOWN_TAGS.has(name)) unknown.add(name);
    for (const attribute of element.attributes) {
      if (/^on[a-z]+$/i.test(attribute.name)) report.inlineHandlers += 1;
    }
  });
  report.customElements = [...custom];
  report.unknownElements = [...unknown];
  return report;
};

const warning = (code, level, values = {}) => ({ code, level, ...values });

/** Compatibility warnings for an analysis report (codes, never silent drops). */
export const buildCompatibilityWarnings = (report, { sizeChars = 0, limitField = "htmlCode" } = {}) => {
  const warnings = [];
  if (!report) return warnings;
  const limit = HWDI_LIMITS[limitField] || HWDI_LIMITS.htmlCode;
  if (sizeChars > limit) warnings.push(warning("SIZE_OVER_LIMIT", "error", { size: sizeChars, limit }));
  if (report.hasElakayMetadata && !report.metadataValid) warnings.push(warning("METADATA_INVALID", "warning"));
  if (report.inlineScripts || report.externalScripts.length) {
    warnings.push(warning("SCRIPTS_ISOLATED", "info", { count: report.inlineScripts + report.externalScripts.length }));
  }
  report.externalScripts.filter((script) => !script.allowed).forEach((script) =>
    warnings.push(warning("EXTERNAL_SCRIPT_BLOCKED", "warning", { host: script.host || script.src }))
  );
  report.stylesheets.filter((sheet) => !sheet.allowed).forEach((sheet) =>
    warnings.push(warning("EXTERNAL_STYLESHEET_BLOCKED", "warning", { host: sheet.host || sheet.href }))
  );
  report.iframes.filter((frame) => frame.kind === "blocked").forEach((frame) =>
    warnings.push(warning("IFRAME_BLOCKED", "warning", { host: frame.host || frame.src }))
  );
  report.iframes.filter((frame) => frame.kind === "invalid-video").forEach((frame) =>
    warnings.push(warning("VIDEO_URL_INVALID", "warning", { url: frame.src }))
  );
  if (report.forms) warnings.push(warning("FORMS_INERT", "info", { count: report.forms }));
  if (report.inlineHandlers) warnings.push(warning("INLINE_HANDLERS", "info", { count: report.inlineHandlers }));
  if (report.unknownElements.length) warnings.push(warning("UNKNOWN_ELEMENTS", "info", { names: report.unknownElements.join(", ") }));
  if (report.customElements.length) warnings.push(warning("CUSTOM_ELEMENTS", "info", { names: report.customElements.join(", ") }));
  if (report.hasBase) warnings.push(warning("BASE_TAG_IGNORED", "info"));
  if (report.hasMetaRefresh) warnings.push(warning("META_REFRESH", "warning"));
  return warnings;
};

/**
 * Everything the import panel needs about a pasted/imported text.
 * `options` = possible destinations; `recommended` = the lossless one.
 */
export const analyzeImport = (input) => {
  const text = String(input ?? "");
  const detection = detectContentFormat(text);
  const sizeChars = text.length;
  const sizeBytes = byteLength(text);
  const base = { ...detection, sizeChars, sizeBytes, report: null, warnings: [], options: [], recommended: null };

  if (detection.format === "empty") return base;

  if (detection.format === "elakay-json") {
    return { ...base, options: ["json"], recommended: "json" };
  }

  if (detection.format === "html-document" || detection.format === "html-fragment") {
    const report = analyzeHtml(text, { fragment: detection.format === "html-fragment" });
    const warnings = buildCompatibilityWarnings(report, { sizeChars, limitField: "htmlCode" });
    const options = ["html"];
    if (detection.format === "html-document" && report.metadataValid) options.push("blocks");
    return { ...base, report, warnings, options, recommended: "html" };
  }

  if (detection.format === "markdown") {
    const warnings = sizeChars > HWDI_LIMITS.sourceText ? [warning("SIZE_OVER_LIMIT", "error", { size: sizeChars, limit: HWDI_LIMITS.sourceText })] : [];
    return { ...base, warnings, options: ["markdown", "html", "text"], recommended: "markdown" };
  }

  const warnings = sizeChars > HWDI_LIMITS.sourceText ? [warning("SIZE_OVER_LIMIT", "error", { size: sizeChars, limit: HWDI_LIMITS.sourceText })] : [];
  return { ...base, warnings, options: ["text", "markdown", "html"], recommended: "text" };
};

/**
 * Converts imported text to the fields of the chosen destination.
 * Returns a partial document ({ htmlCode, sourceFormat, sourceText }) for
 * html/markdown/text; "blocks"/"json" are handled by the Console itself.
 */
export const convertImport = (input, destination, detectedFormat = detectContentFormat(input).format) => {
  const text = String(input ?? "");
  if (destination === "markdown") {
    return { sourceFormat: "markdown", sourceText: text, htmlCode: markdownToStoredHtml(text) };
  }
  if (destination === "text") {
    return { sourceFormat: "text", sourceText: text, htmlCode: plainTextToHtml(text) };
  }
  if (destination === "html") {
    if (detectedFormat === "markdown") return { sourceFormat: "html", htmlCode: markdownToStoredHtml(text) };
    if (detectedFormat === "plain-text") return { sourceFormat: "html", htmlCode: plainTextToHtml(text) };
    // HTML (document or fragment): stored exactly as pasted.
    return { sourceFormat: "html", htmlCode: text };
  }
  return null;
};

/**
 * Inserts a fragment into an existing HTML page without replacing it: inside
 * the Elakay root/<main> when present, else right before </body>, else at
 * the end. Never turns a complete document into a partial one.
 */
export const insertIntoHtmlDocument = (current = "", fragment = "") => {
  const page = String(current || "");
  const addition = String(fragment || "");
  if (!page.trim()) return addition;
  const rootClose = /(<main[^>]*data-elakay-how-we-do-it-root[^>]*>[\s\S]*?)(\n?\s*<\/main>)/i;
  if (rootClose.test(page)) return page.replace(rootClose, (_m, before, after) => `${before}\n${addition}${after}`);
  if (/<\/main>/i.test(page)) return page.replace(/<\/main>(?![\s\S]*<\/main>)/i, `${addition}\n</main>`);
  if (/<\/body>/i.test(page)) return page.replace(/<\/body>(?![\s\S]*<\/body>)/i, `${addition}\n</body>`);
  return `${page}\n${addition}`;
};

// ---------------------------------------------------------------------------
// Blocks-mode fidelity: can a document be kept by the visual builder?
// ---------------------------------------------------------------------------

// Mirrors the server's sanitize-html options for "text" blocks
// (elakay_controllers/howWeDoIt.js SANITIZE_OPTIONS). Only VISIBLE losses
// block the conversion: an element the sanitizer removes, or an inline style
// property it drops. Invisible attributes it strips (id, data-*, aria-*)
// do not change what the visitor sees.
const TEXT_BLOCK_TAGS = new Set(
  (
    "address article aside footer header h1 h2 h3 h4 h5 h6 hgroup main nav section blockquote dd div dl dt figcaption figure hr li " +
    "ol p pre ul a abbr b bdi bdo br cite code data dfn em i kbd mark q rb rp rt rtc ruby s samp small span strong sub sup time u " +
    "var wbr caption col colgroup table tbody td tfoot th thead tr img ins"
  ).split(/\s+/)
);
const TEXT_BLOCK_STYLES = new Set(["color", "background-color", "text-align", "font-family", "font-size", "width", "height", "border-radius"]);

/**
 * Whether `bodyElement`'s content would survive visibly unchanged as ONE
 * text block (the visual builder's fallback for a page without metadata).
 */
export const assessTextBlockFidelity = (bodyElement) => {
  const reasons = new Set();
  if (!bodyElement) return { lossless: true, reasons: [] };
  bodyElement.querySelectorAll("*").forEach((element) => {
    const name = element.localName;
    if (name === "style" || name === "script") return;
    if (!TEXT_BLOCK_TAGS.has(name)) {
      reasons.add(`<${name}>`);
      return;
    }
    const style = element.getAttribute("style");
    if (style) {
      style
        .split(";")
        .map((part) => part.split(":")[0].trim().toLowerCase())
        .filter(Boolean)
        .forEach((property) => {
          if (!TEXT_BLOCK_STYLES.has(property)) reasons.add(`style: ${property}`);
        });
    }
  });
  return { lossless: reasons.size === 0, reasons: [...reasons].slice(0, 12) };
};
